import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { CONFIG } from "../config";
import { ColorPalette } from "@bennara/shared";

/** First mesh hit must be at least this close to the focus along the ray (world units). */
const LINE_OF_SIGHT_TOLERANCE = 0.06;

export class Scene3D {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly controls: OrbitControls;
  readonly canvas: HTMLCanvasElement;
  private readonly ambientLight: THREE.AmbientLight;
  /** Bounding sphere of the body mesh (center + radius); pan/framing use center, pan uses radius context. */
  private bodyBoundingSphere: THREE.Sphere | null = null;
  /** Body GLB root — raycasts exclude directions where nearer mesh blocks the focus. */
  private bodyModelRoot: THREE.Object3D | null = null;
  private readonly raycaster = new THREE.Raycaster();
  private readonly initialCameraPosition: THREE.Vector3;
  private readonly initialControlsTarget = new THREE.Vector3(0, 0, 0);

  constructor(container: HTMLElement, colors: ColorPalette) {
    this.canvas = document.createElement("canvas");
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(colors.background);

    const { fov, near, far, initialPosition } = CONFIG.scene.camera;
    this.initialCameraPosition = new THREE.Vector3(...initialPosition);
    this.camera = new THREE.PerspectiveCamera(
      fov,
      window.innerWidth / window.innerHeight,
      near,
      far,
    );
    this.camera.position.copy(this.initialCameraPosition);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = CONFIG.controls.enableDamping;
    this.controls.dampingFactor = CONFIG.controls.dampingFactor;
    this.controls.minDistance = CONFIG.controls.minDistance;
    this.controls.maxDistance = CONFIG.controls.maxDistance;

    this.ambientLight = new THREE.AmbientLight(0xffffff, 2);
    this.scene.add(this.ambientLight);

    const directional = new THREE.DirectionalLight(0xffffff, 0.8);
    directional.position.set(5, 10, 5);
    directional.castShadow = true;
    this.scene.add(directional);

    container.appendChild(this.canvas);
  }

  render(): void {
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  resize(): void {
    // TODO: Resize throws an error, saying that cant access aspect of undefined
    // this.camera.aspect = window.innerWidth / window.innerHeight;
    // this.camera.updateProjectionMatrix();
    // this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  getSize(): { width: number; height: number } {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  changeTheme(colors: ColorPalette): void {
    this.scene.background = new THREE.Color(colors.background);
  }

  setBodyReferenceFromModel(root: THREE.Object3D): void {
    const box = new THREE.Box3().setFromObject(root);
    if (box.isEmpty()) {
      this.bodyModelRoot = null;
      this.bodyBoundingSphere = null;
      return;
    }
    this.bodyModelRoot = root;
    const sp = new THREE.Sphere();
    box.getBoundingSphere(sp);
    this.bodyBoundingSphere = sp;
  }

  private _objectIsUnderAncestor(
    obj: THREE.Object3D,
    ancestor: THREE.Object3D,
  ): boolean {
    let cur: THREE.Object3D | null = obj;
    while (cur) {
      if (cur === ancestor) {
        return true;
      }
      cur = cur.parent;
    }
    return false;
  }

  /**
   * Ray from camera toward focus: reject if the first mesh hit is clearly in front of the focus
   * (another limb/torso blocking the mole).
   * For **groups**, bbox center is often *inside* the limb volume — the first hit is the limb surface
   * *before* that depth; in that case we accept when that surface belongs to `focusMesh`.
   */
  private _isLineOfSightToFocusClear(
    cameraPos: THREE.Vector3,
    focusPoint: THREE.Vector3,
    focusMesh: THREE.Object3D | null = null,
  ): boolean {
    if (!this.bodyModelRoot) {
      return true;
    }
    const dir = new THREE.Vector3().subVectors(focusPoint, cameraPos);
    const distToFocus = dir.length();
    if (distToFocus < 1e-6) {
      return true;
    }
    dir.multiplyScalar(1 / distToFocus);
    this.raycaster.set(cameraPos, dir);
    const hits = this.raycaster.intersectObject(this.bodyModelRoot, true);
    if (hits.length === 0) {
      return true;
    }
    const first = hits[0];
    if (focusMesh && this._objectIsUnderAncestor(first.object, focusMesh)) {
      return true;
    }
    return first.distance >= distToFocus - LINE_OF_SIGHT_TOLERANCE;
  }

  /**
   * Ensures an unobstructed view from camera to `focusPoint` through the body mesh.
   * Tries `preferred`, then alternatives (closest to `preferred` first), then moves
   * outward along the same direction from `focusPoint` at increasing distance.
   */
  private _resolveCameraWithClearLineOfSight(
    preferred: THREE.Vector3,
    focusPoint: THREE.Vector3,
    validatedAlternatives: THREE.Vector3[],
    baseDistance: number,
    center: THREE.Vector3,
    focusMesh: THREE.Object3D | null = null,
  ): THREE.Vector3 {
    const clear = (p: THREE.Vector3) =>
      this._isLineOfSightToFocusClear(p, focusPoint, focusMesh);

    if (clear(preferred)) {
      return preferred.clone();
    }

    const sorted = [...validatedAlternatives].sort(
      (a, b) => a.distanceTo(preferred) - b.distanceTo(preferred),
    );
    for (const c of sorted) {
      if (clear(c)) {
        return c.clone();
      }
    }

    const dirFromFocus = new THREE.Vector3().subVectors(preferred, focusPoint);
    if (dirFromFocus.lengthSq() < 1e-10) {
      dirFromFocus.subVectors(focusPoint, center);
    }
    dirFromFocus.normalize();

    const maxMul = 10;
    for (let mul = 1.05; mul <= maxMul; mul += 0.05) {
      const p = focusPoint
        .clone()
        .add(dirFromFocus.clone().multiplyScalar(baseDistance * mul));
      if (clear(p)) {
        return p;
      }
    }

    for (const alt of sorted) {
      const d = new THREE.Vector3().subVectors(alt, focusPoint);
      if (d.lengthSq() < 1e-10) continue;
      d.normalize();
      for (let mul = 1.05; mul <= maxMul; mul += 0.05) {
        const p = focusPoint
          .clone()
          .add(d.clone().multiplyScalar(baseDistance * mul));
        if (clear(p)) {
          return p;
        }
      }
    }

    const fallbackDir = new THREE.Vector3().subVectors(focusPoint, center);
    if (fallbackDir.lengthSq() > 1e-10) {
      fallbackDir.normalize();
      for (let mul = 1.05; mul <= maxMul; mul += 0.05) {
        const p = focusPoint
          .clone()
          .add(fallbackDir.clone().multiplyScalar(baseDistance * mul));
        if (clear(p)) {
          return p;
        }
      }
    }

    if (this.bodyBoundingSphere) {
      const C = this.bodyBoundingSphere.center;
      const R = this.bodyBoundingSphere.radius;
      const outward = new THREE.Vector3().subVectors(focusPoint, C);
      if (outward.lengthSq() < 1e-10) {
        outward.set(0, 1, 0);
      }
      outward.normalize();
      for (let mul = 1.2; mul <= 8; mul += 0.1) {
        const p = focusPoint
          .clone()
          .add(outward.clone().multiplyScalar(R * mul));
        if (clear(p)) {
          return p;
        }
      }
    }

    return preferred.clone();
  }

  /**
   * Camera distance so a sphere of radius `r` at the orbit target fits in the view frustum.
   */
  private _distanceToFitSphereRadius(radius: number): number {
    const vFov = THREE.MathUtils.degToRad(this.camera.fov);
    const aspect = this.camera.aspect;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
    const dV = radius / Math.sin(vFov / 2);
    const dH = radius / Math.sin(hFov / 2);
    return Math.max(dV, dH) * 1.15;
  }

  getZoom(): number {
    return this.camera.position.distanceTo(this.controls.target);
  }

  private _hexRingSphereDirections(
    ringCount = 12,
    pointsPerRing = 9,
  ): THREE.Vector3[] {
    const dirs: THREE.Vector3[] = [];
    dirs.push(new THREE.Vector3(0, 1, 0));
    dirs.push(new THREE.Vector3(0, -1, 0));

    for (let r = 0; r < ringCount; r++) {
      const phi = (Math.PI * (r + 1)) / (ringCount + 1);
      const y = Math.cos(phi);
      const ringRadius = Math.sin(phi);
      for (let k = 0; k < pointsPerRing; k++) {
        const theta = (k / pointsPerRing) * Math.PI * 2;
        dirs.push(
          new THREE.Vector3(
            ringRadius * Math.cos(theta),
            y,
            ringRadius * Math.sin(theta),
          ),
        );
      }
    }
    return dirs;
  }

  private _findVisibleCandidates(
    focusPoint: THREE.Vector3,
    baseDistance: number,
    focusMesh: THREE.Object3D | null = null,
  ): THREE.Vector3[] {
    const directions = this._hexRingSphereDirections();
    const candidates: THREE.Vector3[] = [];

    for (const dir of directions) {
      const direction = dir.clone();
      direction.y += 0.2;
      direction.normalize();

      const candidate = focusPoint
        .clone()
        .add(direction.multiplyScalar(baseDistance));

      if (this._isLineOfSightToFocusClear(candidate, focusPoint, focusMesh)) {
        candidates.push(candidate);
      }
    }

    return candidates;
  }

  private _pickPreferredFromCandidates(
    focusPoint: THREE.Vector3,
    candidates: THREE.Vector3[],
    fallbackCenter: THREE.Vector3,
    baseDistance: number,
  ): THREE.Vector3 {
    if (candidates.length === 0) {
      const fallbackDir = focusPoint.clone().sub(fallbackCenter).normalize();
      return focusPoint.clone().add(fallbackDir.multiplyScalar(baseDistance));
    }

    if (candidates.length === 1) {
      return candidates[0].clone();
    }

    const centroid = new THREE.Vector3();
    for (const c of candidates) {
      centroid.add(c);
    }
    centroid.multiplyScalar(1 / candidates.length);

    const fromFocus = centroid.clone().sub(focusPoint);
    if (fromFocus.lengthSq() < 1e-10) {
      return candidates[0].clone();
    }

    return focusPoint
      .clone()
      .add(fromFocus.normalize().multiplyScalar(baseDistance));
  }

  private _animateCamera(
    endPos: THREE.Vector3,
    endTarget: THREE.Vector3,
    speed = 0.02,
  ): void {
    const startPos = this.camera.position.clone();
    const startTarget = this.controls.target.clone();

    let t = 0;

    const animate = () => {
      t += speed;
      const ease = 1 - Math.pow(1 - t, 3);

      this.camera.position.lerpVectors(startPos, endPos, ease);
      this.controls.target.lerpVectors(startTarget, endTarget, ease);

      this.controls.update();

      if (t < 1) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }

  centerOnPoint(point: THREE.Vector3, options?: { preserveZoom?: boolean }) {
    if (!this.bodyModelRoot) return;

    const box = new THREE.Box3().setFromObject(this.bodyModelRoot);
    const modelCenter = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3()).length();

    const baseDistance = options?.preserveZoom
      ? THREE.MathUtils.clamp(
          this.camera.position.distanceTo(this.controls.target),
          CONFIG.controls.minDistance,
          CONFIG.controls.maxDistance,
        )
      : size * 0.35;

    const candidates = this._findVisibleCandidates(point, baseDistance);
    const preferred = this._pickPreferredFromCandidates(
      point,
      candidates,
      modelCenter,
      baseDistance,
    );
    const bestPosition = this._resolveCameraWithClearLineOfSight(
      preferred,
      point,
      candidates,
      baseDistance,
      modelCenter,
    );

    this._animateCamera(bestPosition, point.clone());
  }

  centerOnGroup(group: THREE.Object3D, options?: { preserveZoom?: boolean }) {
    if (!this.bodyModelRoot) return;

    const groupBox = new THREE.Box3().setFromObject(group);
    const focusPoint = groupBox.getCenter(new THREE.Vector3());
    const groupSphere = new THREE.Sphere();
    groupBox.getBoundingSphere(groupSphere);
    const fitRadius = Math.max(groupSphere.radius, 0.02);

    const modelCenter = new THREE.Box3()
      .setFromObject(this.bodyModelRoot)
      .getCenter(new THREE.Vector3());

    const baseDistance = options?.preserveZoom
      ? THREE.MathUtils.clamp(
          this.camera.position.distanceTo(this.controls.target),
          CONFIG.controls.minDistance,
          CONFIG.controls.maxDistance,
        )
      : THREE.MathUtils.clamp(
          this._distanceToFitSphereRadius(fitRadius),
          CONFIG.controls.minDistance,
          CONFIG.controls.maxDistance,
        );

    const candidates = this._findVisibleCandidates(
      focusPoint,
      baseDistance,
      group,
    );
    const preferred = this._pickPreferredFromCandidates(
      focusPoint,
      candidates,
      modelCenter,
      baseDistance,
    );
    const bestPosition = this._resolveCameraWithClearLineOfSight(
      preferred,
      focusPoint,
      candidates,
      baseDistance,
      modelCenter,
      group,
    );

    this._animateCamera(bestPosition, focusPoint.clone());
  }

  resetCameraToInitial() {
    this._animateCamera(
      this.initialCameraPosition.clone(),
      this.initialControlsTarget.clone(),
      0.025,
    );
  }
}
